/**
 * CreatePersonalPage — /create
 *
 * Pure consumer mode. NO wedding language, NO supplier language, NO
 * pricing wall. The upsell loop's destination — students who just
 * experienced prom land here to spin up their own event in seconds.
 *
 * Form is intentionally just two fields (event name + optional date)
 * because the moment of intent is fragile.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Confetti, ArrowRight, QrCode, FilmStrip, Trophy, MusicNote, Cake } from '@phosphor-icons/react';
import { trackGrowth, getAnonId } from '../lib/growthTrack';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUICK_PICKS = [
  { label: '🎂 Birthday', value: '18th Birthday' },
  { label: '🏠 House party', value: 'House Party' },
  { label: '🎓 Uni night', value: 'Uni Night' },
  { label: '✈️ Holiday', value: 'Holiday' },
  { label: '🥂 Reunion', value: 'Reunion' },
];

export default function CreatePersonalPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromSource = params.get('from') || 'create_landing';
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    trackGrowth('upsell_viewed', { metadata: { source: fromSource, variant: 'landing' } });
  }, [fromSource]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Give your event a name to continue.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API}/personal/create`, {
        event_name: name.trim(),
        event_date: date.trim() || '',
        source: fromSource,
        anon_id: getAnonId(),
      });
      const slug = res?.data?.slug;
      if (slug) {
        navigate(`/personal/${slug}/ready`);
      } else {
        setError('Could not create — please try again.');
        setSubmitting(false);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create — please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="create-personal-page">
      <div className="max-w-md mx-auto px-5 py-10">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}>
            <Confetti size={22} weight="fill" className="text-white" />
          </div>

          <h1 className="text-3xl sm:text-4xl font-black leading-tight mb-2" data-testid="create-headline">
            Create your <span style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>own event</span>
          </h1>
          <p className="text-white/65 text-base mb-8 leading-relaxed">
            Photos, videos, messages, games — all in one place.
            Ready in 30 seconds.
          </p>
        </motion.div>

        <form onSubmit={submit} className="space-y-3 mb-6" data-testid="create-form">
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-wider text-white/55 mb-1.5 block">
              What's your event called? <span className="text-[#C29D52]">*</span>
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My 18th Birthday"
              className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-white/30 text-base focus:outline-none focus:border-[#B89762]/40/50"
              data-testid="create-name-input"
              autoFocus
            />
          </label>

          {/* Quick picks — reduces typing friction */}
          <div className="flex flex-wrap gap-2 pt-1" data-testid="create-quick-picks">
            {QUICK_PICKS.map((q) => (
              <button
                key={q.value}
                type="button"
                onClick={() => setName(q.value)}
                className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/8 text-white/75 hover:bg-white/[0.08] hover:text-white transition-colors"
              >
                {q.label}
              </button>
            ))}
          </div>

          <label className="block pt-3">
            <span className="text-[11px] font-black uppercase tracking-wider text-white/55 mb-1.5 block">
              Date <span className="text-white/30 normal-case font-normal tracking-normal">(optional)</span>
            </span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-white text-base focus:outline-none focus:border-[#B89762]/40/50"
              data-testid="create-date-input"
            />
          </label>

          {error && <p className="text-rose-300 text-sm" data-testid="create-error">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-black text-white shadow-lg disabled:opacity-50 mt-2"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="create-submit"
          >
            {submitting ? 'Creating…' : (<>Start in 30 seconds <ArrowRight size={16} weight="bold" /></>)}
          </button>

          <p className="text-[12px] text-center text-white/45 pt-1">
            No login · No payment · Live in seconds
          </p>
        </form>

        <div className="rounded-2xl bg-white/[0.03] border border-white/8 p-5 mt-8" data-testid="create-features-card">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/45 mb-3">What you get</p>
          <ul className="space-y-2.5 text-sm text-white/85">
            <Feature icon={<QrCode size={14} weight="fill" />} text="QR code your guests scan to join" />
            <Feature icon={<FilmStrip size={14} weight="fill" />} text="Live photo & video uploads" />
            <Feature icon={<Trophy size={14} weight="fill" />} text="Vote-driven challenges & leaderboards" />
            <Feature icon={<MusicNote size={14} weight="fill" />} text="Messages, video clips & a final highlight reel" />
            <Feature icon={<Cake size={14} weight="fill" />} text="Works for any event — no setup, no apps" />
          </ul>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, text }) {
  return (
    <li className="flex items-center gap-2.5">
      <span className="shrink-0 w-7 h-7 rounded-full bg-[#B89762]/30/12 text-[#C29D52]/85 flex items-center justify-center">{icon}</span>
      <span>{text}</span>
    </li>
  );
}
